'use client'

import type { ReactTable } from '@tanstack/react-table'
import type { ReactNode } from 'react'

import { TableNoFilterResults } from '@morpho-org/ui-v6/client'
import { TableNoResults } from '@morpho-org/ui-v6/server'

import type { VaultTransactionsTableFeatures } from '@/components/VaultPage/vaultTransactions/config'

import { ErrorDataUnavailable } from '@/components/common/Error'
import { Table, TrBodyRow, TrHeaderGroup } from '@/components/common/Table'
import { VaultTransactionsPartialNotice } from '@/components/VaultPage/vaultTransactions/components/VaultTransactionsPartialNotice'

interface VaultV2TransactionsTableContentProps<TRow extends { typeText: string }> {
  table: ReactTable<VaultTransactionsTableFeatures, TRow>
  loading: boolean
  error?: Error | null
  /** The page left rows out: a notice over its rows, or the error state when none are left. */
  partial?: boolean
  loadingRows: ReactNode[][]
  hasActiveFilters?: boolean
  clearFilters?: () => void
}

const VaultV2TransactionsTableContent = <TRow extends { typeText: string }>({
  table,
  loading,
  error,
  partial = false,
  loadingRows,
  hasActiveFilters = false,
  clearFilters
}: VaultV2TransactionsTableContentProps<TRow>) => {
  // 'use no memo': `table` keeps one reference while its rows change, so a compiled component
  // would cache the rows of an earlier filter.
  'use no memo'
  const hasNoTransactions = table.getRowModel().rows.length === 0

  // Error state
  if (!loading && (error || (partial && hasNoTransactions))) {
    return (
      <ErrorDataUnavailable
        minHeight="478px"
        style={{
          borderRadius: '0'
        }}
      />
    )
  }

  if (hasNoTransactions && !error && !loading) {
    if (hasActiveFilters && clearFilters) {
      return (
        <TableNoFilterResults
          type="transactions"
          clearFilters={clearFilters}
          height="478px"
          justify="center"
        />
      )
    }

    return (
      <TableNoResults text="No transactions found." height="478px" style={{ borderRadius: '0' }} />
    )
  }

  // Table content
  if (!hasNoTransactions || loading) {
    return (
      <>
        {partial && !loading && <VaultTransactionsPartialNotice />}
        <Table.Table tableVariant="compact">
          <Table.Thead>
            {table.getHeaderGroups().map(headerGroup => (
              <TrHeaderGroup key={headerGroup.id} headerGroup={headerGroup} />
            ))}
          </Table.Thead>
          <Table.Tbody>
            {!loading &&
              table
                .getRowModel()
                .rows.map(row => (
                  <TrBodyRow key={`${row.id}-${row.original.typeText}`} row={row} />
                ))}
            {loading &&
              loadingRows.map((row, index) => (
                <TrBodyRow key={`skeleton-${index}`} skeletonRow={row} />
              ))}
          </Table.Tbody>
        </Table.Table>
      </>
    )
  }

  return null
}

export default VaultV2TransactionsTableContent
